/**
 * QuestService — orchestrates count-bounded ("loop") and verify-bounded
 * ("ralph") quests on top of AgentManager.
 *
 * Persistence: file-backed JSON via QuestStore.
 * Streaming: subscribers receive a `QuestRecord` snapshot on every
 * state transition. The WS layer is responsible for fan-out to clients.
 *
 * Restart recovery: any `running` record on boot is rewritten to `stopped`,
 * mirroring loop-service's behavior. In-flight runners do not survive a
 * daemon restart.
 *
 * Runner state in v1:
 *   - "loop" kind  → `runner-loop.ts` runs N cards sequentially.
 *   - "ralph" kind → `runner-ralph.ts` runs worker → verifier until pass
 *                    or maxIterations. Lifts logic from loop-service.
 *
 * The runners are intentionally pluggable (the service holds a strategy per
 * kind) so future kinds (handoff, committee) drop in additively.
 */

import { randomUUID } from "node:crypto";
import type { Logger } from "pino";
import type { AgentManager } from "../agent/agent-manager.js";
import { QuestStore } from "./store.js";
import { getRulesForKind } from "./rules.js";
import {
  QuestCardSchema,
  QuestRecordSchema,
  type CardWiring,
  type QuestCard,
  type QuestKind,
  type QuestRecord,
  type QuestTermination,
} from "./types.js";
import type { AgentProvider } from "../agent/agent-sdk-types.js";
import { scheduleQuestMetadataGeneration } from "./quest-metadata-generator.js";

const QUEST_ID_LENGTH = 8;
const DEFAULT_PROVIDER: AgentProvider = "claude";

export interface QuestRunOptions {
  kind: QuestKind;
  cwd: string;
  prompt: string;
  defaultProvider?: AgentProvider;
  defaultModel?: string | null;
  termination: QuestTermination;
  /**
   * Per-card overrides. Length must match termination.n for "count" kind.
   * Ignored for "ralph" kind (cards append dynamically as iterations run).
   */
  cards?: ReadonlyArray<{
    role?: string | null;
    model?: string | null;
    prompt?: string | null;
  }>;
  summarizerEnabled?: boolean;
  /**
   * auth-server user-id of the creator. Stamped onto the QuestRecord and
   * forwarded to every child agent the runner spawns so per-user filtering
   * keeps the workers scoped to the owner. Required — sessions are always
   * account-bound after the single-tenant cut.
   */
  ownerUserId: string;
  /** Display name of the owner — forwarded to child agents for Claude profile routing. */
  ownerUsername?: string | null;
}

export interface QuestRunner {
  /** Drives the quest to completion. Throws on fatal error; respects abortSignal. */
  execute(args: { questId: string; signal: AbortSignal; service: QuestService }): Promise<void>;
}

export type QuestSubscriber = (record: QuestRecord) => void;

interface RunningQuestState {
  abortController: AbortController;
  promise: Promise<void>;
}

export interface QuestServiceOptions {
  appostleHome: string;
  agentManager: AgentManager;
  logger: Logger;
  /** Strategy registry — populated by callers (bootstrap) before quests run. */
  runners?: Partial<Record<QuestKind, QuestRunner>>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function createQuestId(): string {
  return randomUUID().replace(/-/g, "").slice(0, QUEST_ID_LENGTH);
}

function defaultWiring(): CardWiring {
  return { inputs: "user-prompt", outputs: "none" };
}

/**
 * Builds the initial card array. For "loop"/"run" kinds we pre-render N cards
 * from `termination.n`. For "orchestrate" we pre-render one card per entry in
 * `options.cards` (each card is one available worker; the queen bee chooses
 * dispatch order at runtime). For "ralph" the cards array starts empty and
 * the runner appends as iterations execute.
 */
function buildInitialCards(
  options: QuestRunOptions,
  rulesAllowOverride: { role: boolean; model: boolean; prompt: boolean },
): QuestCard[] {
  if (options.kind === "orchestrate") {
    const overrides = options.cards ?? [];
    return overrides.map((override, i) =>
      QuestCardSchema.parse({
        index: i + 1,
        role: rulesAllowOverride.role ? (override.role ?? null) : null,
        model: rulesAllowOverride.model ? (override.model ?? null) : null,
        prompt: rulesAllowOverride.prompt ? (override.prompt ?? null) : null,
        wiring: defaultWiring(),
        status: "queued",
        agentId: null,
        startedAt: null,
        completedAt: null,
        verify: null,
      }),
    );
  }
  if (options.termination.type !== "count") {
    return [];
  }
  const n = options.termination.n;
  const overrides = options.cards ?? [];
  const cards: QuestCard[] = [];
  for (let i = 0; i < n; i++) {
    const override = overrides[i] ?? {};
    cards.push(
      QuestCardSchema.parse({
        index: i + 1,
        role: rulesAllowOverride.role ? (override.role ?? null) : null,
        model: rulesAllowOverride.model ? (override.model ?? null) : null,
        prompt: rulesAllowOverride.prompt ? (override.prompt ?? null) : null,
        wiring: defaultWiring(),
        status: "queued",
        agentId: null,
        startedAt: null,
        completedAt: null,
        verify: null,
      }),
    );
  }
  return cards;
}

function buildSummarizerCard(options: QuestRunOptions): QuestCard | null {
  if (options.kind !== "loop") {
    return null;
  }
  if (!options.summarizerEnabled) {
    return null;
  }
  return QuestCardSchema.parse({
    index: 0, // sentinel — summarizer is not in the cards array
    role: null,
    model: null,
    prompt: null,
    wiring: { inputs: "all-previous", outputs: "none" },
    status: "queued",
    agentId: null,
    startedAt: null,
    completedAt: null,
    verify: null,
  });
}

export class QuestService {
  private readonly logger: Logger;
  private readonly store: QuestStore;
  private readonly running = new Map<string, RunningQuestState>();
  private readonly subscribers = new Set<QuestSubscriber>();
  private readonly runners: Partial<Record<QuestKind, QuestRunner>>;
  private loaded = false;

  constructor(private readonly options: QuestServiceOptions) {
    this.logger = options.logger.child({ module: "quest-service" });
    this.store = new QuestStore({
      appostleHome: options.appostleHome,
      logger: this.logger,
    });
    this.runners = { ...(options.runners ?? {}) };
  }

  /** Register/replace a runner strategy for a given kind. */
  registerRunner(kind: QuestKind, runner: QuestRunner): void {
    this.runners[kind] = runner;
  }

  /** Internal accessor used by runners — returns `null` if not found. */
  getRecord(id: string): QuestRecord | null {
    return this.store.get(id);
  }

  /** Internal accessor used by runners — exposes the AgentManager once. */
  get agentManager(): AgentManager {
    return this.options.agentManager;
  }

  /** Subscribe to quest state changes. Returns an unsubscribe function. */
  subscribe(callback: QuestSubscriber): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  async initialize(): Promise<void> {
    if (this.loaded) {
      return;
    }
    await this.store.initialize();
    // Recover any "running" records from a previous daemon process — they're
    // stranded since their AbortControllers no longer exist.
    for (const record of this.store.list()) {
      if (record.status !== "running") {
        continue;
      }
      const recovered = QuestRecordSchema.parse({
        ...record,
        status: "stopped",
        updatedAt: nowIso(),
        completedAt: nowIso(),
        stopRequestedAt: record.stopRequestedAt ?? nowIso(),
        cards: record.cards.map((card) =>
          card.status === "running" ? { ...card, status: "stopped", completedAt: nowIso() } : card,
        ),
        summarizerCard:
          record.summarizerCard && record.summarizerCard.status === "running"
            ? { ...record.summarizerCard, status: "stopped", completedAt: nowIso() }
            : record.summarizerCard,
        queenCard:
          record.queenCard && record.queenCard.status === "running"
            ? { ...record.queenCard, status: "stopped", completedAt: nowIso() }
            : record.queenCard,
      });
      await this.store.upsert(recovered);
      this.logger.info(
        { questId: recovered.id },
        "quest-service: recovered running quest → stopped",
      );
    }
    this.loaded = true;
  }

  async run(options: QuestRunOptions): Promise<QuestRecord> {
    await this.initialize();
    const prompt = options.prompt.trim();
    if (!prompt) {
      throw new Error("prompt cannot be empty");
    }
    if (options.kind === "loop" && options.termination.type !== "count") {
      throw new Error('kind "loop" requires termination.type === "count"');
    }
    if (options.kind === "run" && options.termination.type !== "count") {
      throw new Error('kind "run" requires termination.type === "count"');
    }
    if (options.kind === "ralph" && options.termination.type !== "verify") {
      throw new Error('kind "ralph" requires termination.type === "verify"');
    }
    if (options.kind === "orchestrate" && options.termination.type !== "orchestrate") {
      throw new Error('kind "orchestrate" requires termination.type === "orchestrate"');
    }

    const rules = getRulesForKind(options.kind);
    const overrideAllowed = {
      role: rules.perCardRole !== "locked",
      model: rules.perCardModel !== "locked",
      prompt: rules.perCardPrompt !== "locked",
    };

    const createdAt = nowIso();
    const record = QuestRecordSchema.parse({
      id: createQuestId(),
      kind: options.kind,
      rules,
      cwd: options.cwd,
      prompt,
      name: "",
      defaultProvider: options.defaultProvider ?? DEFAULT_PROVIDER,
      defaultModel: options.defaultModel ?? null,
      termination: options.termination,
      cards: buildInitialCards(options, overrideAllowed),
      summarizerCard: buildSummarizerCard(options),
      status: "running",
      createdAt,
      updatedAt: createdAt,
      startedAt: createdAt,
      completedAt: null,
      stopRequestedAt: null,
      ownerUserId: options.ownerUserId,
      ownerUsername: options.ownerUsername ?? null,
    });

    await this.store.upsert(record);
    this.publish(record);

    // Auto-generate a short label off the user prompt via a small internal
    // metadata agent, mirroring the single-agent session naming flow. Runs
    // asynchronously — the quest itself proceeds while the name fills in.
    scheduleQuestMetadataGeneration({
      questService: this,
      questId: record.id,
      cwd: record.cwd,
      prompt,
      logger: this.logger,
      ownerUserId: record.ownerUserId,
    });

    const runner = this.runners[options.kind];
    if (!runner) {
      // No runner registered — mark the quest as failed cleanly so callers
      // get a well-formed record back rather than a hung "running" state.
      const failed = QuestRecordSchema.parse({
        ...record,
        status: "failed",
        updatedAt: nowIso(),
        completedAt: nowIso(),
      });
      await this.store.upsert(failed);
      this.publish(failed);
      throw new Error(`No runner registered for quest kind "${options.kind}"`);
    }

    const abortController = new AbortController();
    const promise = (async () => {
      try {
        await runner.execute({
          questId: record.id,
          signal: abortController.signal,
          service: this,
        });
      } catch (err) {
        this.logger.error({ err, questId: record.id }, "quest-service: runner threw");
        const current = this.store.get(record.id);
        if (current && current.status === "running") {
          await this.markStatus(record.id, "failed");
        }
      } finally {
        this.running.delete(record.id);
      }
    })();
    this.running.set(record.id, { abortController, promise });

    return record;
  }

  /**
   * Per-user ACL gate for every quest RPC. A quest is visible to a caller iff
   * `quest.ownerUserId === userId`. Mirrors `canUserAccessLoop` /
   * `canUserAccessSchedule` — disclosure-safe: mutations throw "not found"
   * (NOT "forbidden") for non-owners to avoid leaking the existence of
   * other users' quests.
   */
  private canUserAccessQuest(record: Pick<QuestRecord, "ownerUserId">, userId: string): boolean {
    if (record.ownerUserId == null) return false;
    return record.ownerUserId === userId;
  }

  list(userId: string): QuestRecord[] {
    return this.store.list().filter((record) => this.canUserAccessQuest(record, userId));
  }

  inspect(id: string, userId: string): QuestRecord | null {
    const record = this.store.get(id);
    if (!record || !this.canUserAccessQuest(record, userId)) {
      return null;
    }
    return record;
  }

  async stop(id: string, userId: string): Promise<QuestRecord> {
    const current = this.store.get(id);
    if (!current || !this.canUserAccessQuest(current, userId)) {
      throw new Error(`Quest "${id}" not found`);
    }
    if (current.status !== "running") {
      return current;
    }
    const stopRequestedAt = nowIso();
    const updated = QuestRecordSchema.parse({
      ...current,
      stopRequestedAt,
      updatedAt: stopRequestedAt,
    });
    await this.store.upsert(updated);
    this.publish(updated);
    const state = this.running.get(id);
    if (state) {
      state.abortController.abort();
      try {
        await state.promise;
      } catch {
        // runner errors are already logged
      }
    }
    return this.store.get(id) ?? updated;
  }

  // -------------------------------------------------------------------------
  // Mutation helpers (used by runners)
  // -------------------------------------------------------------------------

  /** Updates a single card by index (1-based). Throws if not found. */
  async updateCard(
    questId: string,
    cardIndex: number,
    patch: Partial<QuestCard>,
  ): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    const cards = record.cards.map((card) =>
      card.index === cardIndex ? QuestCardSchema.parse({ ...card, ...patch }) : card,
    );
    return this.persistAndPublish(record, { cards });
  }

  /** Append a card (used by ralph runner as iterations execute). */
  async appendCard(questId: string, card: QuestCard): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    const cards = [...record.cards, QuestCardSchema.parse(card)];
    return this.persistAndPublish(record, { cards });
  }

  /** Set the summarizer card (creates or updates). */
  async updateSummarizer(questId: string, patch: Partial<QuestCard>): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    if (!record.summarizerCard) {
      throw new Error(`Quest "${questId}" has no summarizer card`);
    }
    const summarizerCard = QuestCardSchema.parse({
      ...record.summarizerCard,
      ...patch,
    });
    return this.persistAndPublish(record, { summarizerCard });
  }

  /** Set or replace the queen-bee card on an orchestrate quest. */
  async setQueenCard(questId: string, card: QuestCard): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    return this.persistAndPublish(record, { queenCard: QuestCardSchema.parse(card) });
  }

  /** Patch the queen-bee card. Throws if no queen card exists. */
  async updateQueenCard(questId: string, patch: Partial<QuestCard>): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    if (!record.queenCard) {
      throw new Error(`Quest "${questId}" has no queen card`);
    }
    const queenCard = QuestCardSchema.parse({
      ...record.queenCard,
      ...patch,
    });
    return this.persistAndPublish(record, { queenCard });
  }

  /** Record the hivemind doc absolute path for an orchestrate quest. */
  async setHivemindPath(questId: string, hivemindPath: string): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    return this.persistAndPublish(record, { hivemindPath });
  }

  /** Set the auto-generated quest name. No-op if the name hasn't actually changed. */
  async setName(questId: string, name: string): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    if (record.name === name) {
      return record;
    }
    return this.persistAndPublish(record, { name });
  }

  /** Mark the quest's terminal status. Idempotent — running→stopped is fine. */
  async markStatus(questId: string, status: QuestRecord["status"]): Promise<QuestRecord> {
    const record = this.store.get(questId);
    if (!record) {
      throw new Error(`Quest "${questId}" not found`);
    }
    const completedAt = status === "running" ? null : (record.completedAt ?? nowIso());
    return this.persistAndPublish(record, { status, completedAt });
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private async persistAndPublish(
    record: QuestRecord,
    patch: Partial<QuestRecord>,
  ): Promise<QuestRecord> {
    const updated = QuestRecordSchema.parse({
      ...record,
      ...patch,
      updatedAt: nowIso(),
    });
    await this.store.upsert(updated);
    this.publish(updated);
    return updated;
  }

  private publish(record: QuestRecord): void {
    for (const cb of this.subscribers) {
      try {
        cb(record);
      } catch (err) {
        this.logger.warn(
          { err: err instanceof Error ? err.message : String(err) },
          "quest-service: subscriber threw",
        );
      }
    }
  }
}
